import dotPropImmutable from "dot-prop-immutable";
import { OrderedSet } from "immutable";
import { cloneDeep, first } from "lodash-es";
import React, { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useImmer } from "use-immer";
import { Chart, LyricSection } from "~/shared/types/chart";
import { ChartMaker } from '../../../utils/ChartMaker'
import NudgeIndexer from "../../../utils/NudgeIndexer";
import { notify } from "~/components/Notifications";

export const ChartEditContext = React.createContext({} as ChartEditState);
type ChartEditState = ReturnType<typeof useInitChartEditState>
const chartMaker = new ChartMaker

export const useInitChartEditState = ({ chart }: { chart: Chart }) => {
  const [editedChart, setEditedChart] = useImmer(cloneDeep(chart));
  const [history_position, setHistoryPosition] = useState(0)
  const [recentLyricsI, setRecentLyricsI] = useState<number | null>(null)
  const [chartHistory, setChartHistory] = useImmer<Chart[]>([editedChart])
  const [edit_location, setEditLocation] = useState<string | null>(null)
  const max_history_length = 21
  const btnClass = 'px-2 py-1 text-sm rounded transition-colors bg-gray-700 text-gray-100 hover:bg-gray-600'

  const genreCategoryId = 1
  const artistCategoryId = 2
  const styleCategoryId = 3

  const historyIsAtStart = () => (
    chartHistory.length == 0 || history_position === 0
  )

  const historyIsAtEnd = () => (
    chartHistory.length == 0 || history_position === (chartHistory.length - 1)
  )

  const historyGo = (dir: 'back' | 'forward') => {
    let new_position: number | null = null

    if (dir === 'back' && !historyIsAtStart()) {
      new_position = history_position - 1
    } else if (dir === 'forward' && !historyIsAtEnd()) {
      new_position = history_position + 1
    }

    if (new_position != null) {
      const chart = chartHistory[new_position]

      setEditedChart(chart)
      setHistoryPosition(new_position)
    }
  }

  const historyNumItems = (dir: 'back' | 'forward'): number => (
    (dir === 'back') ? history_position : chartHistory.length - history_position - 1
  )

  const addChartToHistory = (new_chart: Chart) => {
    setChartHistory(
      chartHistory => {
        chartHistory.push(new_chart)
        if (chartHistory.length > max_history_length) chartHistory.shift()
        setHistoryPosition(chartHistory.length - 1)
      }
    )
  }

  // when editedChart changes, add it to history (debounced to avoid flooding history)
  const debouncedAddToHistory = useDebouncedCallback(addChartToHistory, 300)

  useEffect(() => {
    debouncedAddToHistory(editedChart)
  }, [editedChart])

  const splitIntoSections = (e: React.ClipboardEvent<HTMLDivElement>, path: string) => {
    e.preventDefault() // don't put text into textarea
    e.persist()
    const regex = /\s*\n\s*\n\s*/ // two line breaks preceded, separated or followed by any amount of white space
    const splitText = e.clipboardData.getData('text').split(regex)

    const lines_in_each = splitText.map(text => text.split(/\n/).length)
    const uniq_line_counts = [...new Set(lines_in_each)].sort().reverse()

    const newSections: LyricSection[] = splitText.map(
      text => ({
        text: text,
        label: getLyricLabelGuess(text, uniq_line_counts)
      })
    )

    setEditedChart(draft => {
      const chartWithUnmergedSections = dotPropImmutable.set(draft, path, newSections)
      return dotPropImmutable.set(chartWithUnmergedSections, 'lyrics', chartWithUnmergedSections.lyrics.flat())
    })
  }

  function setRecentLyricsIndex(i: number | null) {
    setRecentLyricsI(i)
    setTimeout(() => setRecentLyricsI(null), 500)
  }

  function setTempState<T>(setter: (val: T | null) => void, val: T | null, delay = 500) {
    setter(val)
    setTimeout(() => setter(null), delay)
  }

  const getLyricLabelGuess = (text: string, line_counts: number[]): string | undefined => {
    let label = undefined
    const default_labels = ['A', 'B', 'D', 'E', 'F', 'G', 'H']
    const labels_by_content = {
      intro: 'In',
      outro: 'O',
      solo: 'S',
      chorus: 'C',
      refrain: 'R',
      instrumental: 'Ins',
    }

    // Step 1: Try to find label by content

    Object.keys(labels_by_content).some(
      content_word => {
        if (text.toLowerCase().includes(content_word)) {
          label = (labels_by_content as Record<string, string>)[content_word]
          return (true)
        }
      }
    )

    if (label) return label

    // Step 2: Assign a default label by the number of lines in text

    const num_lines = text.split(/\n/).length
    const i_of_length = line_counts.indexOf(num_lines)

    return default_labels[i_of_length]
  }

  const addLyricSectionUnder = (i: number) => {
    setEditedChart(draft => {
      draft.lyrics.splice(i + 1, 0, chartMaker.newLyricSection())
    })
    setTempState(setRecentLyricsIndex, i + 1)
  }

  const deleteLyricSectionAt = (i: number) => {
    setEditedChart(draft => {
      draft.lyrics.splice(i, 1)
    })
  }

  // moves an item up or down in an array
  const nudgeLyrics = (dir: 'up' | 'down', i: number) => {
    const { j, warning } = NudgeIndexer.parse(dir, i, editedChart.lyrics.length)

    if (warning) {
      notify(warning, 'warning')
      return
    }

    setEditedChart(draft => {
      // swaps via destructuring
      [draft.lyrics[i], draft.lyrics[j]] = [draft.lyrics[j], draft.lyrics[i]]
    })
    setTempState(setRecentLyricsIndex, j)
  }

  function sectionColorClass(label: string): string {
    let lables: OrderedSet<string> = OrderedSet([])
    lables = lables.add(label)
    return `section-color-${lables.toArray().indexOf(label)}`
  }

  function setValueAndChart<T extends object, K extends keyof T>(obj: T, attr: K, val: T[K]) {
    obj[attr] = val //obj assumed to be chart or nested obj in chart
    setEditedChart(chart)  //closure
  }

  const setActiveBeat = (path: string) => setEditLocation(path)

  return {
    chart,
    editedChart,
    setEditedChart,
    genreCategoryId,
    artistCategoryId,
    styleCategoryId,
    splitIntoSections,
    recentLyricsI,
    setRecentLyricsIndex,
    edit_location,
    setEditLocation,
    setActiveBeat,
    addLyricSectionUnder,
    deleteLyricSectionAt,
    nudgeLyrics,
    historyIsAtStart,
    historyIsAtEnd,
    historyGo,
    historyNumItems,
    history_position,
    sectionColorClass,
    setValueAndChart,
    btnClass,
  }
};

export const useChartEditContext = () => (
  useContext(ChartEditContext)
)